// src/theme/theme.js
import { BorderColor } from "@mui/icons-material";
import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 1024,
      lg: 1266,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: "#6366f1", // Indigo
      light: "#818cf8",
      dark: "#4f46e5",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#10b981", // Emerald
      light: "#34d399",
      dark: "#059669",
    },
    error: {
      main: "#ef4444",
    },
    warning: {
      main: "#f59e0b",
    },
    info: {
      main: "#3b82f6",
    },
    success: {
      main: "#22c55e",
    },
    background: {
      default: "#f8fafc", // Very light slate
      paper: "#ffffff",
    },
    text: {
      primary: "#1e293b",
      secondary: "#64748b",
    },
    divider: "rgba(226, 232, 240, 0.8)",
  },
  typography: {
    fontFamily: "'Poppins', 'Inter', sans-serif",
    h1: {
      fontSize: "2.25rem",
      fontWeight: 700,
      letterSpacing: "-0.025em",
      color: "#0f172a",
      [createTheme().breakpoints.down("sm")]: {
        fontSize: "1.875rem",
      },
    },
    h2: {
      fontSize: "1.875rem",
      fontWeight: 700,
      letterSpacing: "-0.025em",
      color: "#0f172a",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
      color: "#0f172a",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
      color: "#334155",
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.57,
      color: "#64748b",
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 1px 2px rgba(0, 0, 0, 0.05)",
    "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)",
    ...Array(20).fill("none"),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          padding: "8px 20px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        },
        elevation1: {
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        },
        elevation2: {
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          backgroundColor: "#ffffff",
          borderRadius: 12,
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#f8fafc",
            color: "#64748b",
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.75rem",
            letterSpacing: "0.05em",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f1f5f9",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f1f5f9",
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            transition: "all 0.2s ease-in-out",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#818cf8",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6366f1",
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            padding: "0 8px",
            height: "44px",
            borderRadius: 12,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            transition: "all 0.2s ease",
            "& fieldset": {
              border: "none",
            },
            "&:hover": {
              backgroundColor: "#ffffff",
              borderColor: "#818cf8",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            },
            "&.Mui-focused": {
              backgroundColor: "#ffffff",
              borderColor: "#6366f1",
              boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.1)",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "0.875rem",
          },
          "& .MuiInputLabel-root": {
            color: "#64748b",
            fontSize: '0.875rem',
            "&.Mui-focused": {
              color: "#6366f1",
            },
          },
        },
        paper: {
          marginTop: 8,
          borderRadius: 12,
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          border: "1px solid rgba(226, 232, 240, 0.8)",
        },
        listbox: {
          padding: 8,
          "& .MuiAutocomplete-option": {
            borderRadius: 8,
            padding: "8px 12px",
            marginBottom: 2,
            "&[aria-selected='true']": {
              backgroundColor: "rgba(99, 102, 241, 0.08)",
              color: "#4f46e5",
              fontWeight: 600,
            },
            "&:hover": {
              backgroundColor: "#f1f5f9",
            },
          },
        },
      },
    },
  },
});

export default theme;
